import {
  always,
  evolve,
  filter,
  map,
  path,
  pathOr,
  pipe,
  prop,
  propOr,
  allPass,
  includes,
  both,
  ifElse,
  pathEq,
  find,
  equals,
  __,
  curry,
  difference,
  isEmpty,
  propEq,
  lte,
  complement,
  sortBy,
  nth,
  applySpec,
  toLower,
  isNil,
  anyPass,
  gt,
  and,
  converge,
  split,
  last,
  trim,
  memoizeWith
} from 'ramda'
import { utils } from '@digital-commerce/shared-utils'
import { buildAddonItemsText, buildHSCreditList } from '../../../utils/display'
import { formatCurrency } from '../../../utils/currency'
import {
  filterExistingActions,
  getHSCredits,
  getHSPromoCode,
  getRecurringPromotionsForNoTermService,
  getRecurringPromotionsWhereDurationIsLessThanItemRecurringDuration,
  groupMonthlyItemsByOfferId,
  getCacheKey
} from '../../../utils/cart'
import {
  getDiscountDisplayName,
  buildHSGroupedRecurringDiscountList,
  buildIRPCMonthlyDiscountsList,
  buildHSUnGroupedRecurringDiscountList,
  buildIRPCOneTimeDiscountsList
} from '../../../utils/discount'
import {
  CONSTANTS,
  getHSEppDiscount,
  formatHSEppDiscount,
  getInstallType,
  getServiceWithoutSpecifiedDiscounts,
  hasODUUnitInSmartHub
} from '../../../utils/homeSolutions'

const {
  getMonthlyItemPrice,
  getDisplayName,
  getTopLevelItems,
  _getItems,
  findMonthlyRecurring,
  getMonthlyTotalPrice,
  getFixedMonthlyTotalPrice,
  getItemTotalFirstBill,
  getMonthlyAddons
} = utils.cart

const EASY_PAYMENT_ID = '9151550795513408113'
const SMARTHUB_HARDWARE = '9171981861290438799'
const WIFI_HUB_PRODUCTOFFERING_ID = ['9161934865779731642', '9164005255025659260']
const SMARTHUB_CONNECTION_FEE = '9163039825409415152'

const CHANGE_URLS = {
  CONSUMER_CHANGE_URL: '/shop/home-services/bundle/build-your-own',
  BUSINESS_CHANGE_URL: '/business/small/internet/business-internet/plans',
  AUTH_CHANGE_URL: '/my-account/internet/select-plan/plan',
  SMART_HUB_URL: '/shop/home-services/smart-hub/plans'
}

const isWifiHub = pipe(pathOr('', ['productOffering', 'id']), (item) =>
  WIFI_HUB_PRODUCTOFFERING_ID.includes(item)
)

const isSmartHubHardware = pipe(pathOr('', ['productOffering', 'id']), (id) => SMARTHUB_HARDWARE === id)

const isRouterOrHardware = anyPass([
  pipe(pathOr('', ['productOffering', 'name']), toLower, includes('router')),
  isSmartHubHardware
])

const getInternetHardware = pipe(prop('items'), filter(pipe(prop('tags'), includes('hardware'))))

const getMHDiscount = pipe(
  path(['itemPrice', 'recurring']),
  findMonthlyRecurring,
  propOr([], 'alterations'),
  filterExistingActions,
  find(
    pipe(
      prop('tags'),
      allPass([
        ifElse(isNil, always(false), always(true)),
        ifElse(includes('m-and-h-discount'), always(true), always(false))
      ])
    )
  )
)

const getHSDiscount = pipe(
  path(['itemPrice', 'recurring']),
  findMonthlyRecurring,
  propOr([], 'alterations'),
  filterExistingActions,
  filter(pipe(prop('recurringDuration'), gt(__, 11))),
  find(
    pipe(
      prop('tags'),
      allPass([
        ifElse(isNil, always(false), always(true)),
        includes('internet'),
        includes('discount'),
        ifElse(includes('m-and-h-discount'), always(false), always(true))
      ])
    )
  )
)

const getFirstMonthAddons = filter(
  pipe(
    path(['itemPrice', 'recurring']),
    findMonthlyRecurring,
    allPass([complement(isEmpty), propEq('recurringDuration', 1)])
  )
)

export const getAttributesToAppend = ({
  internetService,
  internetAddons,
  cart,
  isBusinessCart,
  translate
}) => {
  const appendedAttributes = []

  // Credits
  const internetCredits = getHSCredits('internet')(cart)
  internetCredits.forEach((credit) => {
    appendedAttributes.push(getDisplayName(credit))
  })

  // Install type
  const internetInstallType = getInstallType(internetService)
  if (!isBusinessCart && internetInstallType !== 'QUICK_CONNECT') {
    const installTypeText =
      internetInstallType === 'SELF_INSTALL'
        ? translate('item.selfInstall')
        : translate('item.proInstall')

    appendedAttributes.push(installTypeText)
  }

  // Addon items
  if (internetAddons?.length > 0) {
    const addonItemsText = buildAddonItemsText(internetAddons, translate)

    appendedAttributes.push(addonItemsText)
  }

  return appendedAttributes
}

export const buildMonthlyAddons = (addons, translate, lang) => {
  const addonsWithDiscountDisplayList = []
  const monthlyAddons = getMonthlyAddons(addons)

  monthlyAddons?.forEach((addon) => {
    const { amount } = getMonthlyItemPrice(addon)

    addonsWithDiscountDisplayList.push({
      id: addon.id,
      name: getDisplayName(addon),
      price: `${formatCurrency(amount, lang)}/mo.`
    })

    // Gets grouped recurring discounts for each addon
    const addonTermDiscounts = buildHSGroupedRecurringDiscountList(addon, lang, translate)
    addonsWithDiscountDisplayList.push(...addonTermDiscounts)
  })

  return addonsWithDiscountDisplayList
}

export const buildIncludedHardware = (internet, isSmartHubService, lang) => {
  if (isSmartHubService) return []

  const includedHardware = pipe(
    getInternetHardware,
    filter((item) => getMonthlyItemPrice(item).amount === 0)
  )(internet)

  return includedHardware.map((device) => ({
    id: device.id,
    name: `${device.quantity} ${getDisplayName(device)}`,
    price: `${formatCurrency(getMonthlyItemPrice(device).subTotal, lang)}/mo.`
  }))
}

export const buildAdditionalHardware = (internet, isSmartHubService, lang) => {
  if (isSmartHubService) return []

  const additionalHardware = pipe(
    getInternetHardware,
    filter((item) => getMonthlyItemPrice(item).amount > 0),
    groupMonthlyItemsByOfferId
  )(internet)

  return additionalHardware.map((device) => ({
    id: device.id,
    name: `${device.quantity} ${getDisplayName(device)}`,
    price: `${formatCurrency(getMonthlyItemPrice(device).subTotal, lang)}/mo.`
  }))
}

export const getEssentialHardwareItems = (service, isSmartHubODU, isEasyPayment) =>
  pipe(
    prop('items'),
    ifElse(
      allPass([() => isSmartHubODU, () => isEasyPayment]),
      filter(anyPass([isRouterOrHardware, isWifiHub])),
      filter(isRouterOrHardware)
    )
  )(service)

export const buildEssentialHardware = ({ internet, isSmartHubEasyPayment, lang }) => {
  const isODUUnitInSmartHub = hasODUUnitInSmartHub(internet)

  const essentialHardwareItems = getEssentialHardwareItems(
    internet,
    isODUUnitInSmartHub,
    isSmartHubEasyPayment
  )

  return essentialHardwareItems?.map((item, idx) => {
    const itemPrice = isSmartHubEasyPayment
      ? getMonthlyItemPrice(item)
      : getItemTotalFirstBill(item)
    const priceSuffix = isSmartHubEasyPayment ? '/mo.' : ''

    const name = getDisplayName(item)
    const price = `${formatCurrency(itemPrice.amount, lang)}${priceSuffix}`

    return {
      id: `smartHub_devices-${idx}`,
      name,
      price
    }
  })
}

export const isRouterWithEasyPayment = (service) => {
  const hasRouterWithEasyPayment = pipe(
    pathOr([], ['productOffering', 'characteristics']),
    find(allPass([propEq('displayName', 'Purchase Type'), propEq('value', EASY_PAYMENT_ID)])),
    complement(isNil)
  )

  return pipe(prop('items'), find(isRouterOrHardware), hasRouterWithEasyPayment)(service)
}

export const buildRecurringDiscounts = ({
  internet,
  isIRPCFlow,
  isInternetSmartHubService,
  compassDiscountItem,
  translate,
  lang,
  isAffiliates
}) => {
  // IRPC discounts only
  if (isIRPCFlow) return buildIRPCMonthlyDiscountsList(internet, translate, lang, isAffiliates)

  const discountsDisplayList = []

  // Business rule: the following discounts should be displayed as separate line items
  // 1. M&H Discount
  // 2. EPP Discount
  const MHDiscount = getMHDiscount(internet)
  const eppDiscount = getHSEppDiscount(internet)
  // getServiceWithoutSpecifiedDiscounts() removes these discounts from the Internet item so they are not displayed twice
  const itemForRecurringDiscounts = getServiceWithoutSpecifiedDiscounts(
    internet,
    [MHDiscount, eppDiscount],
    true
  )

  const isMTM = pathEq(['contractTerm', 'recurringDuration'], 0)(internet)
  const isCompassMTM = propEq('provisioner', 'COMPASS')(internet) && isMTM
  const renderGroupedDiscounts = !isInternetSmartHubService

  // Render grouped term discounts
  if (!isMTM || isCompassMTM) {
    const monthlyDiscounts = renderGroupedDiscounts
      ? buildHSGroupedRecurringDiscountList(itemForRecurringDiscounts, lang, translate, false)
      : buildHSUnGroupedRecurringDiscountList(itemForRecurringDiscounts, lang, translate, false)

    discountsDisplayList.push(...monthlyDiscounts)
  }

  // Render COMPASS mtm discounts
  if (isCompassMTM) {
    const monthlyDiscounts = renderGroupedDiscounts
      ? buildHSGroupedRecurringDiscountList(itemForRecurringDiscounts, lang, translate, true)
      : buildHSUnGroupedRecurringDiscountList(itemForRecurringDiscounts, lang, translate, true)

    discountsDisplayList.push(...monthlyDiscounts)
  }

  // Render MHDiscount
  if (MHDiscount) {
    discountsDisplayList.push({
      id: MHDiscount.id,
      name: translate('MHDiscountLabel'),
      price: `-${formatCurrency(MHDiscount.amount, lang)}/mo.`,
      prefixIcon: 'DEALS'
    })
  }

  // If internet service is MTM, render HS Discount
  const hsDiscount = internet?.tags?.includes('mtm') && getHSDiscount(internet)
  if (hsDiscount) {
    discountsDisplayList.push({
      id: hsDiscount.id,
      name: getDiscountDisplayName(hsDiscount),
      price: `-${formatCurrency(hsDiscount.amount, lang)}/mo.`,
      prefixIcon: 'DEALS'
    })
  }

  // Compass discount item
  if (compassDiscountItem) {
    const objectPath = ['itemPrice', 'recurring', 0, 'alterations', 0]
    const discountDetails = path(objectPath, compassDiscountItem)
    discountsDisplayList.push({
      id: discountDetails.id,
      name: getDiscountDisplayName(discountDetails),
      price: `-${formatCurrency(discountDetails.amount, lang)}/mo.`,
      prefixIcon: 'DEALS'
    })
  }

  // Render EPP discount
  // Business rule: EPP discount is always displayed last
  if (eppDiscount) {
    discountsDisplayList.push(formatHSEppDiscount(eppDiscount, translate, lang, isAffiliates))
  }

  return discountsDisplayList
}

const _hasSmarthubConnectionFee = pipe(
  pathOr('', ['productOffering', 'id']),
  equals(SMARTHUB_CONNECTION_FEE)
)

const getConnectionFeeItem = (service) =>
  pipe(propOr([], 'items'), find(_hasSmarthubConnectionFee))(service)

const getConnectionFeeAmount = (connectionFeeItem) => {
  if (!connectionFeeItem) return 0

  return getItemTotalFirstBill(connectionFeeItem)?.amount
}

export const buildAdditionalChargesList = ({ internet, isInternetSmartHubService, lang }) => {
  const chargesDisplayList = []

  if (!isInternetSmartHubService) return chargesDisplayList

  // Smarthub connection fee
  const connectionFeeItem = getConnectionFeeItem(internet)
  const name = getDisplayName(connectionFeeItem)

  const amount = getConnectionFeeAmount(connectionFeeItem)

  chargesDisplayList.push({
    id: 'connection-fee',
    name,
    price: `${formatCurrency(amount, lang)}`
  })

  return chargesDisplayList
}

const buildOneTimeDiscounts = (internet, cart, isBusinessCart, isIRPCFlow, lang) => {
  if (isIRPCFlow) return buildIRPCOneTimeDiscountsList(internet, lang)

  const isCompassConsumerMTM =
    converge(and, [
      propEq('provisioner', 'COMPASS'),
      pathEq(['contractTerm', 'recurringDuration'], 0)
    ])(internet) && !isBusinessCart
  if (isCompassConsumerMTM) return []

  const recurringPromos = getRecurringPromotionsForNoTermService(internet)
  const promoCodeItem = getHSPromoCode(cart)

  const getPromoCodeName = pipe(pathOr('', ['productOffering', 'name']), split(':'), last, trim)

  return recurringPromos.map((discount) => {
    const isCouponCode = discount.name === getPromoCodeName(promoCodeItem)
    return {
      id: discount.id,
      name: getDiscountDisplayName(discount),
      price: `-${formatCurrency(discount.amount, lang)}`,
      prefixIcon: isCouponCode ? 'PIGGYBANK' : 'DEALS'
    }
  })
}

export const buildAdditionalCreditsAndDiscounts = ({
  internet,
  cart,
  isIRPCFlow,
  isBusinessCart,
  lang
}) => {
  // Onetime discounts
  const oneTimeDiscounts = buildOneTimeDiscounts(internet, cart, isBusinessCart, isIRPCFlow, lang)

  // Credits
  const credits = buildHSCreditList('internet', cart, lang)

  return [...(oneTimeDiscounts || []), ...credits]
}

export const buildHSAddonsAdditionalCharges = (addons, lang) => {
  // First month addons
  const firstMonthAddons = getFirstMonthAddons(addons)
  const firstMonthAddonsDisplay = firstMonthAddons?.map((addon) => ({
    id: addon.id,
    name: getDisplayName(addon),
    price: `${formatCurrency(getMonthlyItemPrice(addon).amount, lang)}`
  }))

  // HS Addon discounts
  const additionalAddonPromotions =
    getRecurringPromotionsWhereDurationIsLessThanItemRecurringDuration(addons)
  const additionaAddonPromotionsDisplay = additionalAddonPromotions?.map((promo, idx) => ({
    id: `${promo.id}-promotion-${idx}`,
    name: getDiscountDisplayName(promo),
    price: `-${formatCurrency(promo.amount, lang)}`,
    prefixIcon: promo.isCouponCode ? 'PIGGYBANK' : 'DEALS'
  }))

  return [...firstMonthAddonsDisplay, ...additionaAddonPromotionsDisplay]
}

const getTopLevelItemWithItems = curry((cart, services = []) => {
  return pipe(
    getTopLevelItems,
    find(pipe(prop('tags'), pipe(difference(services), isEmpty))),
    evolve({
      items: map((id) => path(['items', id], cart))
    })
  )(cart)
})

const getInternetServiceWithSubItems = (cart) => {
  return getTopLevelItemWithItems(cart, ['service-plan', 'internet'])
}
const getInternetHardwareWithSubItems = (cart) => {
  return getTopLevelItemWithItems(cart, ['internet', 'hardware'])
}

const getInternetWithAllSubItems = (cart) => {
  const internetWithSubItems = getInternetServiceWithSubItems(cart)
  const internetHardwareItems = getInternetHardwareWithSubItems(cart)

  if (internetHardwareItems) internetWithSubItems?.items?.push(internetHardwareItems)

  return internetWithSubItems
}

export const getInternetService = memoizeWith(getCacheKey('HS-Internet'), (cart) => {
  if (!cart?.root?.items) return undefined
  return getInternetWithAllSubItems(cart)
})

export const getChangeUrl = ({
  isAuthenticated,
  isBusinessCart,
  isIrpcFlow,
  isSmartHubService
}) => {
  // Service is SmartHub
  if (isSmartHubService) return CHANGE_URLS.SMART_HUB_URL

  // Unauthenticated Flows
  if (!isAuthenticated && !isBusinessCart) return CHANGE_URLS.CONSUMER_CHANGE_URL
  if (!isAuthenticated && isBusinessCart) return CHANGE_URLS.BUSINESS_CHANGE_URL

  // Authenticated Flows
  // IRPC
  if (isIrpcFlow) return CONSTANTS.FIFA_IRPC.FIFA_IRPC_CHANGE_URL

  // Non IRPC
  return CHANGE_URLS.AUTH_CHANGE_URL
}

export const getInternetMonthlyPrice = ({ internet, compassDiscountItem, isIrpcFlow }) => {
  // Sixth month bill is the first bill where all the discounts in due monthly are applied
  const sixthMonthBill = pipe(
    pathOr([], ['totalPrice', 'recurring']),
    findMonthlyRecurring,
    propOr([], 'recurringBreakdowns'),
    filter(pipe(propOr(1, 'start'), lte(__, 6))),
    filter(complement(both(propEq('start', 1), propEq('duration', 1)))),
    sortBy(propOr(1, 'start')),
    nth(-1)
  )

  // Gets all discount info for the compass discount
  const getHsDiscountData = (hsDiscountItem) => {
    const alterationPath = ['itemPrice', 'recurring', 0, 'alterations', 0]
    const getAlterationName = path([...alterationPath, 'name'])
    const getAlterationDisplayName = path([...alterationPath, 'displayName'])
    const getDiscountAmountFromAlteration = path([...alterationPath, 'amount'])
    const getId = prop('id')

    return applySpec({
      id: getId,
      discountName: ifElse(getAlterationDisplayName, getAlterationDisplayName, getAlterationName),
      discountAmount: getDiscountAmountFromAlteration
    })(hsDiscountItem)
  }

  // When IRPC use the sixMonthBill price
  if (isIrpcFlow) return sixthMonthBill(internet)?.subTotal

  // Different logic to isIrpcFlow, this checks the specific item.
  // This logic might not be required
  const isItemFifaIrpc = allPass([pathEq(['provisioner'], 'FIFA'), pathEq(['action'], 'MODIFY')])(
    internet
  )

  const monthlyPriceDetail = isItemFifaIrpc
    ? getFixedMonthlyTotalPrice(internet)
    : getMonthlyTotalPrice(internet)

  const hsDiscount = pipe(getHsDiscountData, propOr(0, 'discountAmount'))(compassDiscountItem)

  return monthlyPriceDetail.subTotal - hsDiscount
}

export const getCompassDiscountItem = (cart) =>
  pipe(
    _getItems,
    find(
      allPass([pipe(prop('tags'), includes('m-and-h-discount')), propEq('category', 'HS_DISCOUNT')])
    )
  )(cart)

export const shouldRender = (cart) => {
  const service = getInternetService(cart)
  return !isNil(service)
}
